import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { BookingSchema } from '../booking/booking';

/**
 * One booking as **staff** read it (P25d) — the member payload plus the three
 * columns only an operator may see.
 *
 * **`venueName` used to be declared here and now lives on the base** (P25e1).
 * The member gained a bookings list, which draws a building's name for the same
 * reason the oversight table does; declaring the field in two parallel
 * extensions is how one booking acquires two descriptions. Staff still read it —
 * extension is what guarantees that — so nothing on this surface changed shape.
 *
 * ## Why it extends rather than restates
 *
 * `BookingSchema` is the member's payload and `AdminBookingSchema` is a superset
 * of it, so extension is what keeps that true: a field added to the base appears
 * here automatically, and the two can never drift into describing different
 * bookings. It also makes the line enforceable in the other direction — the base
 * is `.strict()`, so a member route that ever widened would fail its own parse
 * rather than quietly ship a cancel reason to the person it is about.
 *
 * ## One schema, not a list shape and a detail shape
 *
 * The oversight table and the booking detail answer the same rows, and no route
 * wants the staff columns without the venue's name. A second schema differing by
 * one field would be a level carrying no information.
 *
 * ## What this does NOT re-check
 *
 * `cancelReason` carries **no length bound**, for the reason `outcomeNotes`
 * states on the base: a read DTO describes what the server sends, so
 * re-validating a write-side bound makes a stored row unreadable the day that
 * bound is lowered (P25a1). The bound lives on the write contract, where the
 * value arrives from a person.
 */
export const AdminBookingSchema = BookingSchema.extend({
  /**
   * Why it was called off, in the canceller's own words. Written by the member,
   * staff and platform paths alike, and `null` when nobody gave one — a member
   * cancelling their own meeting owes no explanation (`BookingCancelSchema`),
   * where a staff cancel is refused without one (`BookingStaffCancelSchema`).
   */
  cancelReason: z.string().nullable(),
  /**
   * Which staff member recorded the outcome. `null` until one is — and `null`
   * **with** an outcome would mean the platform did, the same distinction
   * `cancelledByUserId` draws.
   */
  outcomeRecordedByUserId: UuidSchema.nullable(),
  outcomeRecordedAt: z.iso.datetime().nullable(),
}).strict();

export type AdminBooking = z.infer<typeof AdminBookingSchema>;
